import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useDispatch, useSelector } from "src/redux/store";
import { useSnackbar } from "notistack";
import {
  RejectAdjustmentInternal,
  SignAdjustmentInternal,
  clearPendingSignatures,
} from "src/redux/slices/employee";
import SignaturePad from "src/views/app/UserNormal/employees/EmployeeClockings/SignaturePad";

const REASON_LABELS = {
  FORGOT_IN: "Olvidó fichar la entrada",
  FORGOT_OUT: "Olvidó fichar la salida",
  SYSTEM_FAILURE: "Avería o caída del sistema",
  AGREED_CHANGE: "Cambio acordado",
  OTHER: "Otro motivo",
};

function fmt(dt) {
  if (!dt) return "—";
  try {
    // Backend devuelve ISO sin TZ — añadimos Z para tratarlo como UTC y
    // dejamos al navegador convertir a local.
    const date = new Date(dt.endsWith && dt.endsWith("Z") ? dt : `${dt}Z`);
    return date.toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

/**
 * Modal que aparece tras la identificación del empleado en el kiosk si
 * tiene adjustments PENDING. Le permite firmar o rechazar cada uno ahí
 * mismo en lugar de esperar al email. Incluye un botón "Posponer" — el
 * empleado puede entrar al TPV sin firmar y los pendientes volverán a
 * aparecer en su próxima identificación.
 *
 * Cuando se firma o rechaza un adjustment, el slice lo quita del array;
 * cuando llega a 0, el modal se cierra solo.
 */
export default function PendingSignaturesModal({ employeeId, onClose }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { pendingSignatures } = useSelector((s) => s.employees);

  const [submitting, setSubmitting] = useState(false);
  const [signature, setSignature] = useState("");
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Mostramos el primer adjustment de la pila — al firmar/rechazar se quita
  // del array y aparece el siguiente automáticamente. Si la lista vacía
  // tras la última acción, cerramos.
  const current = pendingSignatures?.[0];
  const remaining = pendingSignatures?.length || 0;

  const resetForNext = () => {
    setSignature("");
    setRejectMode(false);
    setRejectReason("");
  };

  const handleSign = async () => {
    if (!current || submitting) return;
    if (!signature || signature.length < 200) {
      enqueueSnackbar("Firma demasiado corta", { variant: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(SignAdjustmentInternal(current.id, employeeId, signature));
      enqueueSnackbar("Firmado correctamente", { variant: "success" });
      resetForNext();
    } catch (err) {
      enqueueSnackbar("No se pudo firmar. Inténtalo de nuevo.", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!current || submitting) return;
    if (!rejectReason || rejectReason.trim().length < 5) {
      enqueueSnackbar("El motivo debe tener al menos 5 caracteres", {
        variant: "warning",
      });
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(
        RejectAdjustmentInternal(current.id, employeeId, rejectReason.trim()),
      );
      enqueueSnackbar("Cambio rechazado", { variant: "info" });
      resetForNext();
    } catch (err) {
      enqueueSnackbar("No se pudo rechazar. Inténtalo de nuevo.", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostpone = () => {
    // Limpiamos la lista del slice para que el modal no reabra durante esta
    // misma identificación. La próxima vez que el empleado se identifique,
    // el `KioskOverlay` vuelve a hacer fetch y los pendientes reaparecen.
    dispatch(clearPendingSignatures());
    onClose?.();
  };

  // Auto-cierre cuando ya no quedan pendientes.
  React.useEffect(() => {
    if (remaining === 0) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining]);

  if (!current) return null;

  const reasonLabel = REASON_LABELS[current.reasonCode] || current.reasonCode || "—";
  const scope = current.isBreakAdjustment ? "descanso" : "fichaje";

  return (
    <Dialog open={true} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <WarningAmberIcon sx={{ color: "warning.main", fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
            Firma pendiente
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Tu superior modificó tu {scope}. Necesitamos tu conformidad (RD-Ley 8/2019).
          </Typography>
        </Box>
        {remaining > 1 && (
          <Chip label={`${remaining} pendientes`} color="warning" size="small" />
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: "grey.100",
              borderRadius: 1.5,
              borderLeft: "4px solid",
              borderLeftColor: "grey.400",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", letterSpacing: 0.5 }}>
              ANTES
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {fmt(current.previousStart)} → {fmt(current.previousEnd)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.5,
              backgroundColor: "success.lighter",
              borderRadius: 1.5,
              borderLeft: "4px solid",
              borderLeftColor: "success.main",
              backgroundImage: "linear-gradient(to right, rgba(34,197,94,0.08), transparent)",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: "success.dark", letterSpacing: 0.5 }}>
              PROPUESTO
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {fmt(current.newStart)} → {fmt(current.newEnd)}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Solicitado por <strong>{current.editedByName || "—"}</strong>
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              <strong>Motivo:</strong> {reasonLabel}
              {current.reasonText && ` — ${current.reasonText}`}
            </Typography>
          </Box>

          {!rejectMode ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                Firma para confirmar el cambio:
              </Typography>
              <SignaturePad onChange={setSignature} disabled={submitting} placeholder="Firma aquí" />
            </Box>
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                ¿Por qué rechazas el cambio? (mín. 5 caracteres)
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={3}
                placeholder="Ej: las horas no son las que trabajé realmente"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                disabled={submitting}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button onClick={handlePostpone} color="inherit" disabled={submitting}>
          Posponer
        </Button>
        <Stack direction="row" spacing={1}>
          {!rejectMode ? (
            <>
              <Button color="error" onClick={() => setRejectMode(true)} disabled={submitting}>
                Rechazar
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleSign}
                disabled={submitting || !signature}
              >
                Firmar y aceptar
              </Button>
            </>
          ) : (
            <>
              <Button color="inherit" onClick={() => setRejectMode(false)} disabled={submitting}>
                Volver
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleReject}
                disabled={submitting}
              >
                Confirmar rechazo
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
